import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AiTraceItem, TraceEvent } from 'src/app/model/response/ai-chat-response';
import { AiService } from 'src/app/service/ai.service';
import { formatTraceTokens, getTraceFields, getTraceIcon, getTraceSummary, TraceField } from './trace-format';

interface TraceItemState {
  loading: boolean;
  item?: AiTraceItem;
  error?: string;
}

/**
 * Painel de trace do turno (ADM, @debug): resumo por tipo na linha fechada, campos ao
 * expandir e "ver texto" para buscar o texto integral gravado (`ref`) — que segue
 * disponível ao reabrir o chat, enquanto o item não expira.
 */
@Component({
  selector: 'app-trace-events',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="trace-section">
      <div *ngFor="let ev of events; let i = index; trackBy: trackByIndex" class="trace-event" [ngClass]="'trace-type-' + ev.type">
        <button type="button" class="trace-toggle" [attr.aria-expanded]="expanded.has(i)" (click)="toggle(i)">
          <i class="pi trace-icon" [ngClass]="icon(ev.type)"></i>
          <span class="trace-summary" [title]="summary(ev)">{{ summary(ev) }}</span>
          <i class="pi trace-chevron" [ngClass]="expanded.has(i) ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </button>
        <dl *ngIf="expanded.get(i) as fields" class="trace-fields">
          <ng-container *ngTemplateOutlet="rows; context: { $implicit: fields, row: i }"></ng-container>
        </dl>
      </div>
    </div>

    <ng-template #rows let-fields let-row="row">
      <ng-container *ngFor="let f of asFields(fields)">
        <dt>{{ f.label }}</dt>
        <dd>
          {{ f.value }}<span *ngIf="f.warn" class="trace-warn"> {{ f.warn }}</span>
          <button *ngIf="f.ref && sessionId" type="button" class="trace-ver" (click)="toggleText(row, f.ref)">ver texto</button>
        </dd>
        <div *ngIf="f.ref && openTexts.has(textKey(row, f.ref))" class="trace-texto">
          <ng-container *ngIf="items.get(f.ref) as state">
            <span *ngIf="state.loading">carregando…</span>
            <span *ngIf="state.error" class="trace-warn">{{ state.error }}</span>
            <ng-container *ngIf="state.item as item">
              <header>{{ item.kind }} · {{ tokens(item.chars) }} chars<span *ngIf="item.truncated" class="trace-warn"> · cortado na gravação</span></header>{{ item.content }}
            </ng-container>
          </ng-container>
        </div>
        <div *ngIf="f.sub?.length" class="trace-sub">
          <ng-container *ngTemplateOutlet="rows; context: { $implicit: f.sub, row: row }"></ng-container>
        </div>
      </ng-container>
    </ng-template>
  `,
  styles: [`
    .trace-section {
      margin-bottom: 8px;
      border-bottom: 1px solid var(--ftds-border);
      padding-bottom: 8px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .trace-event {
      display: flex;
      flex-direction: column;
    }

    .trace-toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      background: none;
      border: none;
      color: var(--ftds-text-dim);
      font-size: 0.78rem;
      font-family: monospace;
      cursor: pointer;
      padding: 2px 0;
      width: 100%;
      text-align: left;
      transition: color 0.2s;
    }

    .trace-toggle:hover {
      color: var(--ftds-text-muted);
    }

    .trace-icon {
      color: var(--ftds-accent-strong);
      font-size: 0.72rem;
      flex-shrink: 0;
    }

    .trace-summary {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .trace-chevron {
      font-size: 0.65rem;
      flex-shrink: 0;
      opacity: 0.7;
    }

    .trace-type-guardrail .trace-icon {
      color: var(--ftds-warn);
    }

    .trace-fields,
    .trace-sub {
      display: grid;
      grid-template-columns: max-content minmax(0, 1fr);
      gap: 2px 12px;
    }

    .trace-fields {
      margin: 4px 0 6px 20px;
      font-size: 0.74rem;
      font-family: monospace;
      color: var(--ftds-text-dim);
      background: rgba(255, 255, 255, 0.03);
      border-radius: 4px;
      padding: 6px 8px;
    }

    .trace-fields dt {
      color: var(--ftds-text-muted);
    }

    .trace-fields dd {
      margin: 0;
      min-width: 0;
      word-break: break-word;
    }

    .trace-sub {
      grid-column: 1 / -1;
      margin: 4px 0 0;
      padding-left: 10px;
      border-left: 1px solid var(--ftds-border-strong);
    }

    .trace-warn {
      color: var(--ftds-warn);
    }

    .trace-ver {
      background: none;
      border: 1px solid var(--ftds-border-strong);
      color: var(--ftds-text-muted);
      font: inherit;
      font-size: 0.7rem;
      border-radius: 4px;
      padding: 1px 7px;
      cursor: pointer;
      margin-left: 6px;
    }

    .trace-ver:hover {
      color: var(--ftds-text);
      border-color: var(--ftds-text-dim);
    }

    .trace-texto {
      grid-column: 1 / -1;
      margin: 2px 0 4px;
      white-space: pre-wrap;
      word-break: break-word;
      max-height: 220px;
      overflow-y: auto;
      background: rgba(0, 0, 0, 0.35);
      border-radius: 4px;
      padding: 6px 8px;
      color: var(--ftds-text-muted);
      font-size: 0.7rem;
      line-height: 1.45;
    }

    .trace-texto header {
      color: var(--ftds-text-dim);
      margin-bottom: 4px;
      font-size: 0.66rem;
    }
  `],
})
export class TraceEventsComponent {
  @Input() events: TraceEvent[] = [];
  /** Sessão dona dos itens gravados; sem ela (sessão ainda não criada) não há "ver texto". */
  @Input() sessionId: string | null = null;

  /** Campos do detalhe aberto, por índice — calculados uma vez ao expandir. */
  readonly expanded = new Map<number, TraceField[]>();
  /** "ver texto" aberto, por linha + ref: o mesmo ref aparece em mais de uma chamada. */
  readonly openTexts = new Set<string>();
  /** Itens buscados, por ref. */
  readonly items = new Map<string, TraceItemState>();

  readonly icon = getTraceIcon;
  readonly summary = getTraceSummary;
  readonly tokens = formatTraceTokens;

  constructor(private aiService: AiService) {}

  trackByIndex(index: number): number {
    return index;
  }

  /** Tipa o contexto do ng-template recursivo (o `let-` chega como any). */
  asFields(fields: TraceField[]): TraceField[] {
    return fields;
  }

  textKey(row: number, ref: string): string {
    return `${row}|${ref}`;
  }

  toggle(index: number): void {
    if (!this.expanded.delete(index)) {
      this.expanded.set(index, getTraceFields(this.events[index]));
    }
  }

  toggleText(row: number, ref: string): void {
    const key = this.textKey(row, ref);
    if (this.openTexts.delete(key)) return;
    this.openTexts.add(key);

    const cached = this.items.get(ref);
    if (!this.sessionId || (cached && !cached.error)) return;

    this.items.set(ref, { loading: true });
    this.aiService.getTraceItem(this.sessionId, ref).subscribe({
      next: (item) => this.items.set(ref, { loading: false, item: { ...item, content: prettyJson(item.content) } }),
      error: (err: HttpErrorResponse) => this.items.set(ref, { loading: false, error: traceItemError(err) }),
    });
  }
}

/** Resultado de tool e lista de tools chegam como JSON compacto; o resto é texto. */
function prettyJson(content: string): string {
  try {
    const parsed = JSON.parse(content);
    return typeof parsed === 'object' && parsed !== null ? JSON.stringify(parsed, null, 2) : content;
  } catch {
    return content;
  }
}

function traceItemError(err: HttpErrorResponse): string {
  switch (err.status) {
    case 404: return 'item não disponível (expirou ou não foi gravado)';
    case 403: return 'trace indisponível para este usuário';
    default: return 'falha ao buscar o item';
  }
}
